import { crossBackLace, mixBackLace, straightBackLace } from 'src/features/brands/atoms/canvas/rightThrow/glove/back/lace/backLace'
import {
  atomsEngravedOfBackFace,
  atomsEngravedOfCatchFaceOfJaModel,
  atomsEngravedOfCatchFaceOfUsModel,
  atomsLabel
} from 'src/features/brands/atoms/canvas/labels/atoms'
import { Brand, Position, State } from 'src/types'
import {
  crossBackLace as crossBackLaceOfFront,
  straightBackLace as straightBackLaceOfFront
} from 'src/features/brands/atoms/canvas/rightThrow/glove/front/lace/backLace'
import { outfielderDefaultBackLace } from 'src/features/brands/atoms/canvas/rightThrow/glove/back/lace/outfielderDefaultBackLace'
import { outfielderDefaultBackLaceForFront } from 'src/features/brands/atoms/canvas/rightThrow/glove/front/lace/outfielderDefaultBackLace'
import { backStyleChecker } from 'src/util/useModelDesign'
import { seriesChecker, usModelOfAtomsChecker } from 'src/features/brands/atoms/helper'

export const backLaceDrawerOfFront = (
  ctx: CanvasRenderingContext2D,
  backLaceValue: string,
  laceColor: string,
  position: Position,
  productNumber: string
): void => {
  switch (backLaceValue) {
    case 'straight':
      straightBackLaceOfFront(ctx, laceColor, 0, 0, 1)
      straightBackLaceOfFront(ctx, laceColor, 128, -270, 30)
      straightBackLaceOfFront(ctx, laceColor, 222, -260, 30)
      return
    case 'crossAndStraight':
      crossBackLaceOfFront(ctx, laceColor, 5, 0, 1)
      straightBackLaceOfFront(ctx, laceColor, 128, -270, 30)
      straightBackLaceOfFront(ctx, laceColor, 222, -260, 30)
      return
    case 'cross':
      crossBackLaceOfFront(ctx, laceColor, 5, 0, 1)
      crossBackLaceOfFront(ctx, laceColor, 128, -270, 30)
      crossBackLaceOfFront(ctx, laceColor, 225, -265, 30)
      return
    default:
      switch (position) {
        case 'outfielder':
          if (productNumber === 'AGL-801') return
          crossBackLaceOfFront(ctx, laceColor, 5, -15, 1)
          outfielderDefaultBackLaceForFront(ctx, laceColor, 0, 20, 0)
          return
        default:
          return
      }
  }
}

export const backLaceDrawer = (
  ctx: CanvasRenderingContext2D,
  backLaceValue: string,
  laceColor: string,
  position: Position,
  productNumber: string
): void => {
  switch (backLaceValue) {
    case 'straight':
      straightBackLace(ctx, laceColor)
      return
    case 'crossAndStraight':
      mixBackLace(ctx, laceColor)
      return
    case 'cross':
      crossBackLace(ctx, laceColor)
      return
    default:
      switch (position) {
        case 'outfielder':
          if (['AGL-801', 'AGL-NE105'].includes(productNumber)) return
          outfielderDefaultBackLace(ctx, laceColor)
          return
        default:
          return
      }
  }
}

export const brandLabelDrawerForGlove = (ctx: CanvasRenderingContext2D, state: State): void => {
  const { isFirstBackStyle } = backStyleChecker(state.backStyle?.value)
  const isUsModel = usModelOfAtomsChecker(state.baseModel.productNumber)
  if (isFirstBackStyle) {
    return atomsLabel(ctx, state.atomsLabel.value, -80, 170, -8, 0.75)
  }
  const { is2025Apl, is2024Apl, is2025Agl, isDefaultWristBelt } = seriesChecker(state.baseModel.productNumber)
  if (isDefaultWristBelt) {
    return atomsLabel(ctx, state.atomsLabel.value, 50, 60, 0, 0.9)
  }
  if (is2025Apl) {
    atomsLabel(ctx, state.atomsLabel.value, 14, 285, -20, 0.78)
    atomsEngravedOfBackFace(ctx, 20, 5, 0)
    return
  }
  if (is2025Agl || is2024Apl) {
    atomsLabel(ctx, state.atomsLabel.value, -200, 330, -35, 0.82)
    atomsEngravedOfBackFace(ctx, -95, 35, -9)
    return
  }
  if (isUsModel) {
    return atomsLabel(ctx, state?.atomsLabel?.value, 0, 0, 0, 1)
  }
}

export const atomsLabelDrawerForGloveLeftThrow = (ctx: CanvasRenderingContext2D, state: State): void => {
  const { isFirstBackStyle } = backStyleChecker(state.backStyle?.value)
  const productNumber = state.baseModel.productNumber
  if (isFirstBackStyle) {
    return atomsLabel(ctx, state.atomsLabel.value, 500, -155, 15, 0.75)
  }
  const { is2025Apl, is2024Apl, is2025Agl, isDefaultWristBelt } = seriesChecker(state.baseModel.productNumber)
  if (isDefaultWristBelt) {
    return atomsLabel(ctx, state.atomsLabel.value, 40, 0, 0, 1)
  }
  if (is2025Apl) {
    atomsLabel(ctx, state.atomsLabel.value, 265, -230, 30, 0.82)
    atomsEngravedOfBackFace(ctx, 65, 5, 0)
    return
  }
  if (is2025Agl || is2024Apl) {
    atomsLabel(ctx, state.atomsLabel.value, 265, -290, 35, 0.82)
    atomsEngravedOfBackFace(ctx, 90, -20, 0)
    return
  }

  if (usModelOfAtomsChecker(productNumber)) {
    atomsLabel(ctx, state?.atomsLabel?.value, 90, -40, 5, 1)
    return
  }
  // atomsLabel(ctx, state?.atomsLabel?.value, 90, -45, 5, 1)
}

export const atomsLabelDrawerForCatcherMitt = (ctx: CanvasRenderingContext2D, state: State): void => {
  const productNumber = state.baseModel.productNumber
  if (['APL-UR002+'].includes(productNumber)) {
    atomsLabel(ctx, state.atomsLabel.value, 50, 150, -15, 0.85)
    atomsEngravedOfBackFace(ctx, -15, 45, 0)
  }
  if (productNumber !== 'APL-UR002+') {
    if (state.backStyle?.value === 'beltBackStyle') {
      atomsLabel(ctx, state.atomsLabel.value, 30, 155, 0, 0.85)
    }
    if (state.backStyle?.value === 'regularBackStyle') {
      atomsLabel(ctx, state.atomsLabel.value, 20, 40, 0, 0.9)
    }
  }
}

export const atomsLabelDrawerForCatcherMittLeftThrow = (ctx: CanvasRenderingContext2D, state: State): void => {
  if (['APL-UR002+'].includes(state.baseModel.productNumber)) {
    atomsLabel(ctx, state.atomsLabel.value, 120, -150, 18, 0.85)
    atomsEngravedOfBackFace(ctx, 100, 45, 0)
  }
  if (state.baseModel.productNumber !== 'APL-UR002+') {
    if (state.backStyle?.value === 'beltBackStyle') {
      atomsLabel(ctx, state.atomsLabel.value, 170, 155, 0, 0.85)
    }
    if (state.backStyle?.value === 'regularBackStyle') {
      atomsLabel(ctx, state.atomsLabel.value, 150, 0, 4, 0.9)
    }
  }
}

// メーカー捕球面の刻印
export const engravedDrawer = (ctx: CanvasRenderingContext2D, brand: Brand, catchFaceColor: string, productNumber: string): void => {
  const isUsModel = usModelOfAtomsChecker(productNumber)
  switch (brand) {
    case 'atoms':
      switch (isUsModel) {
        case true:
          atomsEngravedOfCatchFaceOfUsModel(ctx, 0, 0, catchFaceColor)
          break
        case false:
          atomsEngravedOfCatchFaceOfJaModel(ctx, 0, 0, catchFaceColor)
          break
      }
      break
  }
}

export const engravedDrawerLeftThrow = (ctx: CanvasRenderingContext2D, brand: Brand, catchFaceColor: string, productNumber: string): void => {
  const isUsModel = usModelOfAtomsChecker(productNumber)
  switch (brand) {
    case 'atoms':
      switch (isUsModel) {
        case true:
          atomsEngravedOfCatchFaceOfUsModel(ctx, -50, 0, catchFaceColor)
          break
        case false:
          atomsEngravedOfCatchFaceOfJaModel(ctx, -50, 0, catchFaceColor)
          break
      }
      break
  }
}
